/**
 * Icones d'interface, en SVG inline.
 *
 * Inline et pas sprite : elles sont peu nombreuses, elles heritent de
 * `currentColor` (donc de l'etat presse du bouton) et elles n'ajoutent aucune
 * requete. Toutes sont decoratives : le sens est porte par l'aria-label du
 * bouton qui les contient.
 */
import type { ReactElement } from 'react';

const BASE = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  focusable: false,
} as const;

export type IconProps = { readonly size?: number };

/** Pot de peinture. */
export function FillIcon({ size = 20 }: IconProps): ReactElement {
  return (
    <svg {...BASE} width={size} height={size}>
      <path d="M6.5 8.5 12 3l6 6-5.5 5.5a1.4 1.4 0 0 1-2 0l-4-4a1.4 1.4 0 0 1 0-2Z" />
      <path d="M9 6 7 4" />
      <path d="M20 14c1.2 1.6 1.8 2.7 1.8 3.4a1.8 1.8 0 1 1-3.6 0c0-.7.6-1.8 1.8-3.4Z" />
    </svg>
  );
}

/** Pipette. */
export function EyedropperIcon({ size = 20 }: IconProps): ReactElement {
  return (
    <svg {...BASE} width={size} height={size}>
      <path d="m15.5 5.5 3 3" />
      <path d="M17 3.5a2.1 2.1 0 0 1 3 3l-2.4 2.4-3-3Z" />
      <path d="m14.6 6.9 2.5 2.5-7.4 7.4-3.2.7.7-3.2Z" />
      <path d="M5 19h6" />
    </svg>
  );
}

/** Gomme. */
export function EraserIcon({ size = 20 }: IconProps): ReactElement {
  return (
    <svg {...BASE} width={size} height={size}>
      <path d="M8.5 19H21" />
      <path d="M14.5 3.9 3.9 14.5a1.9 1.9 0 0 0 0 2.7l2.9 2.9h4.3l9-9a1.9 1.9 0 0 0 0-2.7l-2.9-2.9a1.9 1.9 0 0 0-2.7 0Z" />
      <path d="m9.5 8.9 5.6 5.6" />
    </svg>
  );
}

/** Main : deplacement de la vue. */
export function HandIcon({ size = 20 }: IconProps): ReactElement {
  return (
    <svg {...BASE} width={size} height={size}>
      <path d="M10 11V4.6a1.3 1.3 0 0 1 2.6 0V11" />
      <path d="M12.6 10.8V3.9a1.3 1.3 0 0 1 2.6 0V11" />
      <path d="M15.2 11.4V6.2a1.3 1.3 0 0 1 2.6 0V14c0 3.6-2.4 6.6-6 6.6-2 0-3.3-.8-4.4-2.2l-3-4.2a1.3 1.3 0 0 1 2-1.7l1.6 1.7V6.6a1.3 1.3 0 0 1 2.6 0V11" />
    </svg>
  );
}

/** Annuler le dernier trait. */
export function UndoIcon({ size = 20 }: IconProps): ReactElement {
  return (
    <svg {...BASE} width={size} height={size}>
      <path d="M4 9h9.5a5.5 5.5 0 0 1 0 11H9" />
      <path d="m7.5 5.5-3.5 3.5 3.5 3.5" />
    </svg>
  );
}

/**
 * Boite a outils : ouvre le panneau des outils sur la barre compacte.
 *
 * Fixe, et volontairement pas l'icone de l'outil courant : celle du pinceau,
 * penchee et effilee, se confondait avec la pipette juste a cote. Un contenant
 * ne peut pas etre pris pour l'un de ses outils. L'outil actif, lui, se lit a
 * la teinte du bouton.
 */
export function ToolboxIcon({ size = 20 }: IconProps): ReactElement {
  return (
    <svg {...BASE} width={size} height={size}>
      <path d="M9 7V5.5a1.5 1.5 0 0 1 1.5-1.5h3A1.5 1.5 0 0 1 15 5.5V7" />
      <rect x="3" y="7" width="18" height="12" rx="2" />
      <path d="M3 12.5h18" />
      <path d="M10.5 12.5v2h3v-2" />
    </svg>
  );
}

/** Pinceau : l'outil par defaut, nomme dans la barre compacte. */
export function BrushIcon({ size = 20 }: IconProps): ReactElement {
  return (
    <svg {...BASE} width={size} height={size}>
      <path d="M15.5 4.5 19 8l-8.5 8.5-3.5-3.5Z" />
      <path d="M6.5 13.5c-1.6.6-2.4 2-2.6 4.6 2.6-.2 4-1 4.6-2.6" />
    </svg>
  );
}

/** Tableau des scores : la languette du tiroir sur ecran etroit. */
export function ScoresIcon({ size = 18 }: IconProps): ReactElement {
  return (
    <svg {...BASE} width={size} height={size}>
      <path d="M4 20V10M10 20V5M16 20v-7M22 20H2" />
    </svg>
  );
}

/** Coche : la decision est prise, le dessin part. */
export function CheckIcon({ size = 18 }: IconProps): ReactElement {
  return (
    <svg {...BASE} width={size} height={size} strokeWidth={2.4}>
      <path d="m4.5 12.5 5 5 10-11" />
    </svg>
  );
}

/**
 * Œil : marque le joueur que l'on est en train de regarder. Il n'apparait que
 * sur un seul nom a la fois, c'est ce qui en fait un reperage et pas une
 * decoration.
 */
export function EyeIcon({ size = 14 }: IconProps): ReactElement {
  return (
    <svg {...BASE} width={size} height={size} strokeWidth={1.8}>
      <path d="M2 12s3.6-6 10-6 10 6 10 6-3.6 6-10 6-10-6-10-6Z" />
      <circle cx="12" cy="12" r="2.6" />
    </svg>
  );
}

/**
 * Robot : marque l'adversaire automatique de la partie rapide.
 *
 * Le jeu ne fait pas passer le bot pour un humain. Son pseudo est genere comme
 * celui d'un joueur, mais cette icone le suit partout ou son nom apparait.
 */
export function RobotIcon({ size = 14 }: IconProps): ReactElement {
  return (
    <svg {...BASE} width={size} height={size} strokeWidth={1.8}>
      <path d="M12 3v3" />
      <rect x="4" y="6.5" width="16" height="12" rx="3" />
      <circle cx="9" cy="12" r="1.3" />
      <circle cx="15" cy="12" r="1.3" />
      <path d="M9.5 15.6h5" />
      <path d="M2 11v3M22 11v3" />
    </svg>
  );
}

/**
 * Cadeau : marque les modes ENCORE gratuits, en attendant qu'ils deviennent
 * payants. La couronne ci-dessous prendra sa place le jour ou ils le seront ;
 * les deux coexistent volontairement, le basculement doit etre un changement
 * d'icone et rien d'autre.
 */
export function GiftIcon({ size = 13 }: IconProps): ReactElement {
  return (
    <svg {...BASE} width={size} height={size} strokeWidth={1.9}>
      <path d="M3.5 11.5h17V20a1 1 0 0 1-1 1h-15a1 1 0 0 1-1-1v-8.5Z" />
      <path d="M2.5 7.5h19v4h-19z" />
      <path d="M12 7.5V21" />
      <path d="M12 7.5S10.8 3 8.3 3a2.2 2.2 0 0 0 0 4.5Z" />
      <path d="M12 7.5S13.2 3 15.7 3a2.2 2.2 0 0 1 0 4.5Z" />
    </svg>
  );
}

/**
 * Globe : marque le selecteur de langue.
 *
 * Un globe et pas un drapeau : un drapeau designe un pays, jamais une langue
 * (cf. LANGUAGE_NAMES).
 */
export function GlobeIcon({ size = 16 }: IconProps): ReactElement {
  return (
    <svg {...BASE} width={size} height={size}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3.2 9.5h17.6M3.2 14.5h17.6" />
      <path d="M12 3c2.3 2.4 3.5 5.4 3.5 9s-1.2 6.6-3.5 9c-2.3-2.4-3.5-5.4-3.5-9S9.7 5.4 12 3Z" />
    </svg>
  );
}

/**
 * Camera : marque le STREAMER du mode stream.
 *
 * Meme role que l'icone robot pour le bot : ce joueur ne joue pas comme les
 * autres (il ne dessine pas, il ne marque pas), et cela doit se voir partout ou
 * son pseudo apparait.
 */
export function StreamIcon({ size = 13 }: IconProps): ReactElement {
  return (
    <svg {...BASE} width={size} height={size} strokeWidth={1.9}>
      <rect x="2.5" y="7" width="12" height="10" rx="2" />
      <path d="m14.5 12 5-3.2v6.4L14.5 12Z" />
    </svg>
  );
}

/** Fleche vers un plateau : telecharger un fichier. */
export function DownloadIcon({ size = 16 }: IconProps): ReactElement {
  return (
    <svg {...BASE} width={size} height={size} strokeWidth={1.9}>
      <path d="M12 3v11" />
      <path d="m7.5 10.5 4.5 4.5 4.5-4.5" />
      <path d="M4 20h16" />
    </svg>
  );
}

/** Buste : l'acces au compte, quand aucun avatar n'a ete choisi. */
export function UserIcon({ size = 18 }: IconProps): ReactElement {
  return (
    <svg {...BASE} width={size} height={size} strokeWidth={1.8}>
      <circle cx="12" cy="8.5" r="3.8" />
      <path d="M4.5 20c0-3.6 3.4-5.8 7.5-5.8s7.5 2.2 7.5 5.8" />
    </svg>
  );
}

/** Chevron vers la gauche : sortir d'un ecran. */
export function ChevronLeftIcon({ size = 16 }: IconProps): ReactElement {
  return (
    <svg {...BASE} width={size} height={size} strokeWidth={2}>
      <path d="m15 5-7 7 7 7" />
    </svg>
  );
}

/** Croix : ferme un panneau. La sortie doit se VOIR, pas se deviner. */
export function CloseIcon({ size = 18 }: IconProps): ReactElement {
  return (
    <svg {...BASE} width={size} height={size} strokeWidth={2}>
      <path d="m6 6 12 12M18 6 6 18" />
    </svg>
  );
}

/** Chevron vers le bas : affordance d'ouverture d'une liste. */
export function ChevronDownIcon({ size = 14 }: IconProps): ReactElement {
  return (
    <svg {...BASE} width={size} height={size} strokeWidth={2}>
      <path d="m5 9 7 7 7-7" />
    </svg>
  );
}

/**
 * Couronne : marque les modes qui deviendront payants a la creation de partie.
 * Purement decorative, le sens est porte par l'infobulle qui l'accompagne.
 */
export function CrownIcon({ size = 13 }: IconProps): ReactElement {
  return (
    <svg {...BASE} width={size} height={size} strokeWidth={2}>
      <path d="M3 8.5 6.5 12 12 4.5 17.5 12 21 8.5V18a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8.5Z" />
    </svg>
  );
}

/**
 * Logo Discord.
 *
 * REMPLI, pas trace : les marques ont une forme, et un contour de 1,7 px la
 * deforme au point qu'on ne la reconnait plus a 20 px. Il faut donc annuler le
 * `stroke` et poser `fill`, contrairement a toutes les icones au-dessus.
 */
export function DiscordIcon({ size = 20 }: IconProps): ReactElement {
  return (
    <svg {...BASE} width={size} height={size} stroke="none" fill="currentColor">
      <path d="M19.3 5.6a16.4 16.4 0 0 0-4.1-1.3l-.2.4a15 15 0 0 1 3.6 1.2 13.4 13.4 0 0 0-11.2 0 15 15 0 0 1 3.6-1.2l-.2-.4a16.4 16.4 0 0 0-4.1 1.3C3.5 9.4 2.7 13.1 3.1 16.7a16.6 16.6 0 0 0 5 2.6l.7-1.1a10.8 10.8 0 0 1-1.7-.8l.4-.3a11.8 11.8 0 0 0 10 0l.4.3a10.8 10.8 0 0 1-1.7.8l.7 1.1a16.6 16.6 0 0 0 5-2.6c.5-4.2-.8-7.8-2.6-11.1ZM9.3 14.5c-1 0-1.8-.9-1.8-2s.8-2 1.8-2 1.8.9 1.8 2-.8 2-1.8 2Zm5.4 0c-1 0-1.8-.9-1.8-2s.8-2 1.8-2 1.8.9 1.8 2-.8 2-1.8 2Z" />
    </svg>
  );
}

/** Logo Instagram : l'appareil photo carre, trace celui-la. */
export function InstagramIcon({ size = 20 }: IconProps): ReactElement {
  return (
    <svg {...BASE} width={size} height={size}>
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.2" cy="6.8" r="1.1" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Petit tableau : sert a dire ce qu'une pastille numerotee selectionne. */
export function PaintingIcon({ size = 16 }: IconProps): ReactElement {
  return (
    <svg {...BASE} width={size} height={size} strokeWidth={1.9}>
      <rect x="3" y="4" width="18" height="14" rx="2" />
      <path d="m6 15 3.5-4 2.5 3 2-2.5L18 15" />
    </svg>
  );
}
